import * as d3 from 'd3';
import { range } from 'lodash';
import {
  MAIN_GROUP_CONTAINER_SELECTOR,
  PARENT_CHILD_REFERENCE,
} from '@ardoq/global-consts';
import { createFifoCache, returnZero } from '@ardoq/common-helpers';
import { MARKER_PROPS } from '@ardoq/icons';
import { ArdoqId } from '@ardoq/api-types';
import type { Point } from '@ardoq/math';
import {
  CONTEXT_NODE_RADIUS,
  LABEL_MAX_WIDTH,
  LIBACKGROUND_STROKE_WIDTH,
  NODE_RADIUS,
} from './consts';
import type { Zoom, RelationshipsNode, RelationshipsLink } from './types';
import { GraphItem } from '../graphItems/GraphItem';
import type { GraphEdgeBase } from '../graphItems/GraphEdgeBase';
import type { GetDistinctReferenceKeyArgs } from '../viewModel/types';
import { REFERENCE_PATH_STROKE_WIDTH } from '../visuals/consts';
import type { Vector } from '../types';
import { bloat } from './bloat';
import { truncateSvgText } from '../truncateText/truncateSvgText';
import { truncateComponentLabel } from '../truncateText/truncateComponentLabel';
import { getComponentLabelParts } from '../truncateText/getComponentLabelParts';
import { measureSvgText } from '../truncateText/measureSvgText';

const vectorFrom = (from: Point, to: Point): Vector => [
  to[0] - from[0],
  to[1] - from[1],
];

const scaleVector = (vector: Vector, scale: number): Vector => [
  scale * vector[0],
  scale * vector[1],
];

const vectorSum = (a: Point | Vector, b: Point | Vector): Vector => [
  a[0] + b[0],
  a[1] + b[1],
];

const vectorMagnitude = (a: Vector): number =>
  Math.sqrt(a[0] * a[0] + a[1] * a[1]);

const unitVector = (vector: Vector): Vector =>
  scaleVector(
    vector,
    1 / Math.sqrt(vector[0] * vector[0] + vector[1] * vector[1])
  );

const scaleVectorTo = (vector: Vector, length: number) =>
  scaleVector(unitVector(vector), length);

const MARGIN = 5;
export const createGroupPathProvider = (
  groupChildren: Map<string, RelationshipsNode[]>
) => {
  const getChildren = (group: RelationshipsNode) =>
    groupChildren.get(group.id) ?? null;

  const nodeBounds: (node: RelationshipsNode) => Point[] = createFifoCache(
    Infinity,
    (node: RelationshipsNode) => bound(node, getChildren, MARGIN, nodeBounds)
  );
  return (node: RelationshipsNode) =>
    d3.line().curve(d3.curveLinearClosed)(nodeBounds(node));
};
const isContextGraphNode = (
  graphNode: GraphItem,
  selectedComponentId?: ArdoqId | null
) => graphNode.modelId === selectedComponentId;

export const nodeRadius = (
  graphNode: GraphItem,
  selectedComponentId?: ArdoqId | null
) =>
  isContextGraphNode(graphNode, selectedComponentId)
    ? CONTEXT_NODE_RADIUS
    : NODE_RADIUS;

interface RadiusOffsetVectorArgs {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  radius: number;
}
const radiusOffsetVector = ({
  x1,
  x2,
  y1,
  y2,
  radius,
}: RadiusOffsetVectorArgs) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
  const ratio = (d - radius) / d;

  return {
    dx: dx * ratio,
    dy: dy * ratio,
  };
};
interface PathFormulaArgs<
  T extends {
    x?: number;
    y?: number;
  },
> {
  target: T;
  source: T;
  edge: GraphEdgeBase;
  similarLinksIndex?: number;
  similarLinksCount?: number;
}

const isOdd = (a: number): boolean => a % 2 === 1;

type Spline = [
  startX: number,
  startY: number,
  cpX: number,
  cpY: number,
  endX: number,
  endY: number,
];
export const spline = (
  p0: Point,
  r0: number,
  p1: Point,
  r1: number,
  index: number,
  count: number
): Spline => {
  /* create connector spline for the indexth item of count between p0 and p1 */

  const baseVector = vectorFrom(p0, p1);
  const bow = [-baseVector[1], baseVector[0]];

  if (bow[0] > 0 || (bow[0] === 0 && bow[1] > 0)) {
    /* invert bow vector direction */

    bow[0] = -bow[0];
    bow[1] = -bow[1];
  }

  const normalisedBowCenter = r0 / (r1 + r0);
  const perConnectorPixelBowVector =
    (2 * Math.max(r1, r0)) / vectorMagnitude(baseVector);
  const perConnectorPixelBow =
    (isOdd(index) ? 1 : -1) *
    (isOdd(count) ? Math.floor((index + 1) / 2) : 0.5 + Math.floor(index / 2));

  const sc: Point = [
    p0[0] +
      baseVector[0] * normalisedBowCenter +
      bow[0] * perConnectorPixelBow * perConnectorPixelBowVector,
    p0[1] +
      baseVector[1] * normalisedBowCenter +
      bow[1] * perConnectorPixelBow * perConnectorPixelBowVector,
  ];

  const s0 = vectorSum(p0, scaleVectorTo(vectorFrom(p0, sc), r0));
  const s1 = vectorSum(p1, scaleVectorTo(vectorFrom(p1, sc), r1));

  return [s0[0], s0[1], sc[0], sc[1], s1[0], s1[1]];
};

const pathMidPoint =
  <
    T extends {
      x?: number;
      y?: number;
    },
  >(
    straightLines: boolean,
    radiusFormula: (node: T, selectedComponentId?: ArdoqId | null) => number
  ) =>
  (
    {
      target,
      source,
      similarLinksIndex = 0,
      similarLinksCount = 1,
    }: PathFormulaArgs<T>,
    selectedComponentId?: ArdoqId | null
  ) => {
    const { x: targetX = 0, y: targetY = 0 } = target;
    const { x: sourceX = 0, y: sourceY = 0 } = source;

    if (sourceX === targetX && sourceY === targetY) {
      return '';
    }

    if (straightLines && similarLinksCount > 1) {
      /* evaluate quadratic spline midpoint */

      const p0: Vector = [sourceX, sourceY];
      const r0 = radiusFormula(source, selectedComponentId);

      const p1: Vector = [targetX, targetY];
      const r1 = radiusFormula(target, selectedComponentId);

      const s = spline(p0, r0, p1, r1, similarLinksIndex, similarLinksCount);
      const t = 0.5;

      return [
        (1 - t) * (1 - t) * s[0] + 2 * (1 - t) * t * s[2] + t * t * s[4],
        (1 - t) * (1 - t) * s[1] + 2 * (1 - t) * t * s[3] + t * t * s[5],
      ];
    }
    /* evaluate geometric midpoint */

    return [(sourceX + targetX) * 0.5, (sourceY + targetY) * 0.5];
  };

const pathFormula =
  <
    T extends {
      x?: number;
      y?: number;
    },
  >(
    straightLines: boolean,
    radiusFormula: (node: T, selectedComponentId?: ArdoqId | null) => number
  ) =>
  (
    {
      target,
      source,
      edge,
      similarLinksIndex = 0,
      similarLinksCount = 1,
    }: PathFormulaArgs<T>,
    isHover: boolean,
    selectedComponentId?: ArdoqId | null
  ) => {
    let { x: targetX = 0, y: targetY = 0 } = target;
    let { x: sourceX = 0, y: sourceY = 0 } = source;

    let targetRadius = radiusFormula(target, selectedComponentId);
    let sourceRadius = radiusFormula(source, selectedComponentId);

    if (isHover) {
      const endingOffset =
        REFERENCE_PATH_STROKE_WIDTH *
        (MARKER_PROPS[edge.getLineEnding()].markerWidth -
          MARKER_PROPS[edge.getLineEnding()].refX);

      const startingOffset =
        REFERENCE_PATH_STROKE_WIDTH *
        MARKER_PROPS[edge.getLineBeginning()].refX;

      targetRadius += endingOffset + LIBACKGROUND_STROKE_WIDTH / 2;
      sourceRadius += startingOffset + LIBACKGROUND_STROKE_WIDTH / 2;
    }

    if (sourceX === targetX && sourceY === targetY) {
      return '';
    }

    if (straightLines && similarLinksCount > 1) {
      /* create quadratic spline */

      const p0: Vector = [sourceX, sourceY];
      const r0 = radiusFormula(source, selectedComponentId);

      const p1: Vector = [targetX, targetY];
      const r1 = radiusFormula(target, selectedComponentId);

      const s = spline(p0, r0, p1, r1, similarLinksIndex, similarLinksCount);

      return `M${s[0]},${s[1]} Q${s[2]},${s[3]} ${s[4]},${s[5]}`;
    }
    /* fall through to what there was before */

    const sourceRadiusVector = radiusOffsetVector({
      x1: sourceX,
      x2: targetX,
      y1: sourceY,
      y2: targetY,
      radius: sourceRadius,
    });
    sourceX += targetX - sourceX - sourceRadiusVector.dx;
    sourceY += targetY - sourceY - sourceRadiusVector.dy;

    const targetRadiusVector = radiusOffsetVector({
      x1: sourceX,
      x2: targetX,
      y1: sourceY,
      y2: targetY,
      radius: targetRadius,
    });
    targetX -= targetX - sourceX - targetRadiusVector.dx;
    targetY -= targetY - sourceY - targetRadiusVector.dy;

    if (isNaN(sourceX) || isNaN(sourceY) || isNaN(targetX) || isNaN(targetY)) {
      return '';
    }

    let lineFormula = `M${sourceX},${sourceY}L${targetX},${targetY}`;

    if (!straightLines) {
      const dx = targetX - sourceX;
      const dy = targetY - sourceY;
      const dr = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
      lineFormula = `M${sourceX},${sourceY}A${dr},${dr} 0 0,1 ${targetX},${targetY}`;
    }
    return lineFormula;
  };
export const groupDistanceAtDepth = (
  itemDepth: number,
  maxGroupDepth: number
) =>
  CONTEXT_NODE_RADIUS * (maxGroupDepth + 1 - itemDepth) + CONTEXT_NODE_RADIUS;

export const itemDepth = (item: GraphItem): number =>
  1 + (item.parent ? itemDepth(item.parent) : 0);

export const getPathData = pathFormula<RelationshipsNode>(true, nodeRadius);
export const getPathMidPoint = pathMidPoint<RelationshipsNode>(
  true,
  nodeRadius
);

const TRANSITION_DURATION = 500;
// The container can hold multiple SVGs (e.g. zoom-control sprite icons that
// render earlier in DOM order), so a plain `select('svg')` picks the wrong one.
// Pick the SVG that owns the relationship view's main group.
export const selectSvg = (container: HTMLDivElement | null) =>
  d3
    .select(container)
    .select<SVGSVGElement>(`svg:has(${MAIN_GROUP_CONTAINER_SELECTOR})`);
export const selectSvgTransition = (container: HTMLDivElement | null) =>
  selectSvg(container).transition().duration(TRANSITION_DURATION);

interface FitWindowArgs {
  container: HTMLDivElement;
  zoom: Zoom;
  margin: number;
}

export const fitWindow = ({ container, zoom, margin }: FitWindowArgs) => {
  const viewBounds = container.getBoundingClientRect();
  const simulationBounds = d3
    .select(container)
    .select<SVGGElement>(MAIN_GROUP_CONTAINER_SELECTOR)
    .node()
    ?.getBBox();

  const simulationLeft = simulationBounds?.x ?? 0;
  const simulationTop = simulationBounds?.y ?? 0;
  const simulationWidth = simulationBounds?.width ?? 0;
  const simulationHeight = simulationBounds?.height ?? 0;
  const scale = Math.min(
    viewBounds.width / (simulationWidth + margin),
    viewBounds.height / (simulationHeight + margin)
  );
  const simulationCenterX = simulationLeft + simulationWidth / 2;
  const simulationCenterY = simulationTop + simulationHeight / 2;
  const translateX = viewBounds.width / 2 - scale * simulationCenterX;
  const translateY = viewBounds.height / 2 - scale * simulationCenterY;

  selectSvgTransition(container).call(
    zoom.transform,
    d3.zoomIdentity.translate(translateX, translateY).scale(scale)
  );
};
export const fitWindowMargin = (maxGroupDepth: number) =>
  2 * CONTEXT_NODE_RADIUS + groupDistanceAtDepth(maxGroupDepth, maxGroupDepth);

const RESOLUTION = 100;
const DOUBLE_PI = Math.PI * 2;

const bound = (
  node: RelationshipsNode,
  children: (n: RelationshipsNode) => RelationshipsNode[] | null,
  margin: number,
  boundWithCaching: (node: RelationshipsNode) => Point[],
  selectedComponentId?: ArdoqId | null
): Point[] => {
  const radius = nodeRadius(node, selectedComponentId);

  /* the leaf circle could profitably be cached according to radius */
  const leafCircle: Point[] = range(0, RESOLUTION).map(step => {
    const theta = (DOUBLE_PI * step) / (RESOLUTION - 1);
    return [
      radius * Math.cos(theta) + (node.x ?? 0),
      radius * Math.sin(theta) + (node.y ?? 0),
    ];
  });

  const nodeChildren = children(node);
  if (!nodeChildren) {
    return leafCircle;
  }
  return bloat(
    d3.polygonHull([
      ...leafCircle,
      ...nodeChildren.flatMap(child => boundWithCaching(child)),
    ]) ?? leafCircle,
    margin
  );
};

export const relationshipsViewDistinctReferenceKey = ({
  isParentChildReference,
  globalTypeId,
  sourceGraphNodeId,
  targetGraphNodeId,
}: GetDistinctReferenceKeyArgs) =>
  isParentChildReference
    ? `${PARENT_CHILD_REFERENCE}-${sourceGraphNodeId}-${targetGraphNodeId}`
    : `${globalTypeId},${sourceGraphNodeId},${targetGraphNodeId}`;

export const processSimilarLinks = <
  T extends Pick<
    RelationshipsLink,
    'similarLinksCount' | 'similarLinksIndex'
  > & {
    source: { id: string };
    target: { id: string };
  },
>(
  link: T,
  linksBySourceAndTarget: Map<string, T[]>
) => {
  const sourceAndTargetId = `${link.source.id}~~${link.target.id}`;
  const targetAndSourceId = `${link.target.id}~~${link.source.id}`;
  const hadSourceAndTargetMatchingLinks =
    linksBySourceAndTarget.has(sourceAndTargetId);
  const sourceAndTargetMatchingLinks = hadSourceAndTargetMatchingLinks
    ? linksBySourceAndTarget.get(sourceAndTargetId)!
    : [link];
  if (hadSourceAndTargetMatchingLinks) {
    sourceAndTargetMatchingLinks.push(link);
  } else {
    linksBySourceAndTarget.set(sourceAndTargetId, sourceAndTargetMatchingLinks);
  }

  const targetAndSourceMatchingLinks =
    linksBySourceAndTarget.get(targetAndSourceId);

  const similarLinks = [
    ...(sourceAndTargetMatchingLinks ?? []),
    ...(targetAndSourceMatchingLinks ?? []),
  ];
  link.similarLinksIndex = similarLinks.length - 1;
  similarLinks.forEach(link => (link.similarLinksCount = similarLinks.length));
};
export const getGraphNodeLabel = (graphNode: RelationshipsNode) => {
  const isComponent = graphNode.isComponent();
  const isGroup = graphNode.isGroup();

  if (!(isComponent || isGroup)) {
    return {};
  }

  const labelParts = isComponent
    ? getComponentLabelParts(graphNode.modelId)
    : null;

  const fullLabel = labelParts
    ? truncateComponentLabel({
        ...labelParts,
        measure: returnZero,
        width: Infinity,
      })
    : graphNode.getLabel();

  const truncatedLabel = labelParts
    ? truncateComponentLabel({
        ...labelParts,
        measure: measureSvgText,
        width: LABEL_MAX_WIDTH,
      })
    : truncateSvgText(fullLabel, LABEL_MAX_WIDTH);

  const isLabelTruncated = fullLabel && truncatedLabel !== fullLabel;

  return { fullLabel, truncatedLabel, isLabelTruncated };
};
